import {Component, Input} from '@angular/core';
import {
  ClassResponse,
  EventResponse,
  HorseRegistryResponse,
  MembershipPlanResponse,
  SponsorResponse
} from '../../../../../../../../../../@shared/models/response.module';
import {ClassesService} from '../../../../../../../../../../@shared/services/classes-service';
import {EventsService} from '../../../../../../../../../../@shared/services/events-service';
import {ScoringService} from '../../../../../../../../../../@shared/services/scoring.service';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {_AQH_EVE_17, _AQH_EVE_7, AQH_CLS_0} from '../../../../../../../../../../@shared/models/input.module';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {StaticService} from '../../../../../../../../../../@shared/services/static-service';
import {firstValueFrom} from 'rxjs';
import { AddSponsor } from '../../../manage-event-sponsor/components/add-sponsor/add-sponsor';
import {ToastService} from '../../../../../../../../../../@core/services/toast.service';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-add-class',
  standalone: false,
  templateUrl: './add-class.html',
  styleUrl: './add-class.css'
})
export class AddClass {
  @Input() event: EventResponse
  @Input() selectedDate: Date | null = null;

  classTypes: ClassResponse[]
  horseRegistries: HorseRegistryResponse[]
  membershipPlans: MembershipPlanResponse[]

  selectedSponsors: SponsorResponse[] = [];

  vatRateOptions = [
    { value: 0, label: '0%' },
    { value: 12.5, label: '12.5%' },
    { value: 25, label: '25%' }
  ];

  currencyOptions = [
    { code: 'NOK', name: 'NOK' },
    { code: 'SEK', name: 'SEK' },
    { code: 'EUR', name: 'EUR' },
    { code: 'USD', name: 'USD' },
    { code: 'GBP', name: 'GBP' },
    { code: 'DKK', name: 'DKK' },
  ];

  scoreTypeOptions: { value: string; label: string }[] = [];

  form: FormGroup;
  saving = false;
  isFree = false;

  constructor(private classService: ClassesService,
              private eventService: EventsService,
              private scoringService: ScoringService,
              private staticService: StaticService,
              private fb: FormBuilder,
              private modal: NgbActiveModal,
              private modalService: NgbModal,
              private toastService: ToastService,
              private translateService: TranslateService) {
    this.form = this.fb.group({
      class_name: ['', [Validators.required, Validators.maxLength(200)]],
      class_description: [''],
      parent_class_id: [null],
      max_num_registrations: [null],
      start_date_time: ['', Validators.required],
      req_membership_plans: [[] as number[]],
      req_horse_registries: [[] as any[]],
      max_participant_age: [null],
      min_horse_age: [null],
      time_per_participant_seconds: [null],
      price: [null],
      price_currency: ['NOK'],
      vat_rate: [0],
      // Scoring configuration
      score_type: ['timed'],
      create_round: [true],
    });
  }



  ngOnInit() {
    this.initScoringOptions();
    this.getClassTypes();
    this.getHorseRegistries();
    this.getMembershipPlans();
    this.initializeDateTime();
    this.initializeCurrency();
  }

  initializeCurrency() {
    // Set currency from event's default_currency (which was set during event creation)
    if (this.event?.default_currency) {
      this.form.get('price_currency')?.setValue(this.event.default_currency);
    }
  }

  initScoringOptions() {
    this.scoreTypeOptions = [
      { value: 'timed', label: this.translateService.instant('scoring.timed') },
      { value: 'points_high', label: this.translateService.instant('scoring.pointsHigh') },
      { value: 'points_low', label: this.translateService.instant('scoring.pointsLow') }
    ];
  }

  initializeDateTime() {
    if (this.selectedDate) {
      // Pre-fill with selected date at 10:00
      const date = new Date(this.selectedDate);
      date.setHours(10, 0, 0, 0);
      const formatted = this.formatDateTimeLocal(date);
      this.form.get('start_date_time')?.setValue(formatted);
    } else if (this.event?.start_date) {
      // Use event start date at 10:00
      const date = new Date(this.event.start_date);
      date.setHours(10, 0, 0, 0);
      const formatted = this.formatDateTimeLocal(date);
      this.form.get('start_date_time')?.setValue(formatted);
    }
  }

  formatDateTimeLocal(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  getClassTypes() {
    let payload: AQH_CLS_0 = {}
    this.classService.getClasses(payload).subscribe((data) => {
      this.classTypes = data.data
    });
  }

  getHorseRegistries() {
    return this.staticService.getHorseRegistries().subscribe((res) => {
      this.horseRegistries = res.data;
    });
  }

  getMembershipPlans() {
    this.staticService.getMembershipPlans().subscribe((res) => {
      console.log("Membership Plans:", res);
      this.membershipPlans = res;
    })
  }

  v(name: string) {
    return this.form.get(name)?.value;
  }
  set(name: string, value: any) {
    this.form.get(name)?.setValue(value);
    this.form.get(name)?.markAsDirty();
  }

  setMembershipIds(ids: number[]) {
    this.set('req_membership_plan_ids', ids);
  }

  toggleFree(checked: boolean) {
    this.isFree = checked;
    if (checked) {
      this.set('price', null);
      this.set('vat_rate', 0);
    }
  }

  removeSponsor(sponsor: SponsorResponse) {
    this.selectedSponsors = this.selectedSponsors.filter(s => s.sponsor_id !== sponsor.sponsor_id);
  }

  async save() {
    if (this.form.invalid || !this.event?.event_id) {
      this.form.markAllAsTouched();
      this.toastService.error(this.translateService.instant('manageEvent.addClass.validationError'));
      return;
    }
    this.saving = true;

    try {
      const payload: _AQH_EVE_7 = {
        event_id: this.event.event_id,
        parent_class_id: this.v('parent_class_id')?.class_id ?? undefined,
        class_name: this.v('class_name'),
        class_description: this.v('class_description'),
        max_num_registrations: this.v('max_num_registrations') ?? undefined,
        start_date_time: this.v('start_date_time'),
        req_membership_plan_ids: (this.v('req_membership_plans') || []).map((p: any) => p.plan_id).filter((id: number | null | undefined) => id !== null && id !== undefined && id >= 0),
        req_horse_registry_ids: (this.v('req_horse_registries') || []).map((r: any) => r.registry_id).filter((id: number | null | undefined) => id !== null && id !== undefined && id >= 0),
        max_participant_age: this.v('max_participant_age') ?? undefined,
        min_horse_age: this.v('min_horse_age') ?? undefined,
        time_per_participant_seconds: this.v('time_per_participant_seconds') ?? undefined,
        price: this.v('price') ?? undefined,
        price_currency: this.v('price_currency') ?? undefined,
        vat_rate: this.v('vat_rate') ?? 0,
      };
      console.log("payload:", payload);

      // Create the event class first
      const res = await firstValueFrom(this.eventService.addEventClass(payload));
      console.log("Class added:", res);

      const event_class_id = res.data?.event_class_id;

      if (event_class_id) {
        // Create scoring configuration
        await firstValueFrom(this.scoringService.saveScoringConfig({
          event_class_id: event_class_id,
          score_type: this.v('score_type'),
          penalty_type: 'time', // Default penalty type
          time_weight: 1,
          points_weight: 1,
          decimal_places: 3
        }));

        // Create default round if selected
        if (this.v('create_round')) {
          await firstValueFrom(this.scoringService.createRound({
            event_class_id: event_class_id,
            round_number: 1,
            round_name: 'Round 1'
          }));
        }

        // Add sponsors if selected
        if (this.selectedSponsors.length > 0) {
          for (const sponsor of this.selectedSponsors) {
            const sponsorPayload: _AQH_EVE_17 = {
              event_class_id: event_class_id,
              sponsor_id: sponsor.sponsor_id
            };
            await firstValueFrom(this.eventService.addSponsorToEventClass(sponsorPayload));
          }
        }
      }

      this.saving = false;
      this.toastService.success(this.translateService.instant('manageEvent.addClass.success'));
      this.modal.close(true);
    } catch (error: any) {
      console.error('Error saving class:', error);
      this.saving = false;
      this.toastService.error(error?.error?.reason || this.translateService.instant('manageEvent.addClass.error'));
    }
  }

  openCreateSponsorModal() {
    const modalRef = this.modalService.open(AddSponsor, { size: 'lg', centered: true });
    modalRef.componentInstance.event = this.event;

    modalRef.result.then((result: SponsorResponse) => {
      if (result && result.sponsor_id) {
        // Create the sponsor object
        const newSponsor: SponsorResponse = {
          sponsor_id: result.sponsor_id,
          sponsor_name: result.sponsor_name || 'New Sponsor',
          sponsor_description: result.sponsor_description || '',
          logo_url: result.logo_url,
          image_url: result.image_url
        };

        // Add to selected sponsors list by creating a new array (triggers change detection)
        if (!this.selectedSponsors.find(s => s.sponsor_id === newSponsor.sponsor_id)) {
          this.selectedSponsors = [...this.selectedSponsors, newSponsor];
        }
      }
    }, () => {});
  }

  close() {
    this.modal.close()
  }

  setHorseRegistries(event: any) {

  }

  /**
   * Calculate net amount after platform + payment fees
   * Stripe: 2.4% + 2.00 NOK, Platform: 2%
   * Net = price × 0.956 - 2.00
   */
  calculateNetAmount(price: number): number {
    if (!price || price <= 0) return 0;
    const net = price * 0.956 - 2;
    return Math.max(0, Math.round(net * 100) / 100);
  }

}
